import * as React from 'react'
import { useLocation } from 'react-router-dom'

import { useClassNames } from '@factorialco/deprecated-design-system'
import { AnimatePresence, Variants, motion } from 'framer-motion'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import useNavigationMatch from 'modules/core/hooks/useNavigationMatch'
import type { SectionLevel, SectionSize } from 'modules/core/lib/navigation/types'

import { useLayoutContext } from 'modules/layout/hooks/useFullscreen'

import * as styles from './index.vanilla.css'

export type Direction = 'up' | 'down'

interface SectionType {
  size: SectionSize
  level: SectionLevel
}

const defaultSection: SectionType = {
  size: 'regular',
  level: 1,
}

export const useSection = (): SectionType => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()
  const matches = useNavigationMatch(navigation, pathname)
  const lastMatch = matches.length ? matches[matches.length - 1] : {}
  const { size, level } = { ...defaultSection, ...lastMatch }

  return { size, level }
}

const transition = {
  duration: 0.6,
  type: 'linear',
}

const backVariants = (levelOffset: number) => ({
  initial: {
    opacity: 0,
    scale: 1,
    y: 0,
    boxShadow: 'inset 0 0 0 100px rgba(0, 0, 0, 0)',
  },
  animate: (level: SectionLevel) => ({
    opacity: 1,
    // This moves the Back element up following a progression, starting at -10px and decreasing.
    y: -((level - levelOffset - (level - levelOffset - 1) * 0.3) * 10),
    scale: 1 - (level - levelOffset) * 0.04,
    boxShadow: `inset 0 0 0 100px rgba(0, 0, 0, ${0.1 * (level - levelOffset)})`,
  }),
  exit: {
    opacity: 0,
    scale: 1,
    y: 0,
    boxShadow: 'inset 0 0 0 100px rgba(0, 0, 0, 0)',
  },
})

const Back = ({ level, levelOffset }: { level: SectionLevel; levelOffset: number }) => {
  const cx = useClassNames(styles)

  return (
    <motion.div
      className={cx('header')}
      transition={transition}
      variants={backVariants(levelOffset)}
      initial="initial"
      animate="animate"
      exit="exit"
      custom={level}
    />
  )
}

const cardVariants: Variants = {
  initial: (direction: Direction) => ({
    opacity: 0,
    scale: direction === 'up' ? 1.04 : 0.96,
    y: direction === 'up' ? 30 : -10,
    position: 'absolute',
  }),
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    position: 'absolute',
    transitionEnd: {
      position: 'static',
    },
  },
  exit: (direction: Direction) => ({
    opacity: 0,
    scale: direction === 'up' ? 0.96 : 1.04,
    y: direction === 'up' ? -10 : 30,
    position: 'absolute',
  }),
}

interface CardProps {
  level: SectionLevel
  size: SectionSize
  direction: Direction
}

const Card = ({ children, level, size, direction }: React.PropsWithChildren<CardProps>) => {
  const cx = useClassNames(styles)
  const { isFullScreen } = useLayoutContext()

  return (
    <motion.div
      className={cx('card', size === 'full' && 'full', isFullScreen && 'cardFullScreen')}
      transition={transition}
      variants={cardVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      custom={direction}
      aria-level={level}
    >
      {children}
    </motion.div>
  )
}

const Container = ({ children }: React.PropsWithChildren) => {
  const cx = useClassNames(styles)

  return (
    <motion.div className={cx('container')} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {children}
    </motion.div>
  )
}

const SectionContainer = ({ children }: React.PropsWithChildren) => {
  const { size, level } = useSection()
  const prevLevelRef = React.useRef(level)

  const direction = useMemo(() => {
    const value = level < prevLevelRef.current ? 'down' : 'up'
    prevLevelRef.current = level

    return value
  }, [level])

  // Create an array with back sections, i.e with level 3: [1, 2]
  const backSections = Array.from({ length: level - 1 }, (_, i) => i + 2)

  return (
    <Container>
      <ErrorBoundary>
        <AnimatePresence initial={false} custom={direction}>
          {backSections.map((backLevel) => (
            <Back key={`back-${backLevel}`} level={level} levelOffset={backLevel - 1} />
          ))}
          <Card key={`level-${level}`} level={level} size={size} direction={direction}>
            {children}
          </Card>
        </AnimatePresence>
      </ErrorBoundary>
    </Container>
  )
}

export default SectionContainer
